import { useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { postHeroImage } from "../../../js/api/my-style/post-hero-image";
import { postFocalPoint } from "../../../js/api/member-media/focal-point";
import { SharedIcon } from "../shared/shared-icon";
import { CENTRE, FocalPointPicker } from "../shared/focal-point-picker";
import { sizeRuleMessage, tooLargeMessage } from "../../../js/upload-limits";

/**
 * Minimum source dimensions. The cover renders at 1920x600 on the public profile, so
 * anything under this upscales into a visibly soft banner. Checked client-side before the
 * framing stage so the member is told immediately rather than after a round trip.
 */
const MIN_WIDTH = 1200;
const MIN_HEIGHT = 675;

const ACCEPTED_TYPES = ["JPG", "PNG", "JPEG"];

/**
 * The shapes the cover is actually cut to: the public profile banner (GetCropUrl 1920x600) and
 * the preview on My Profile (1200x675). The framing is chosen against both at once.
 */
const COVER_PREVIEWS = [
  { label: "Profile banner", aspect: 1920 / 600 },
  { label: "My profile preview", aspect: 1200 / 675 },
];

/**
 * T212647 requires the accepted formats, minimum dimensions and maximum size to be stated
 * AT THE POINT OF UPLOAD, not only in an error after the fact. This is that statement; the
 * matching validation lives in onSelectFile below, so the two cannot drift.
 *
 * The size figure is the CONFIGURED limit (see upload-limits.js), not a MAX_SIZE_MB constant
 * that had to be kept in step with the server by hand — T212719.
 */
const UploaderInner = ({ maxFileSizeBytes }) => {
  return (
    <div className="hero-image__uploader-inner">
      <p>
        <span className="hero-image__uploader-inner--drag">
          Drag photos here or{" "}
        </span>
        <span className="hero-image__uploader-inner--browse">
          browse files
        </span>
      </p>

      <p data-testid="cover-image-requirements">
        PNG, JPG or JPEG.
        <br />
        At least {MIN_WIDTH} x {MIN_HEIGHT}px.
        <br />
        {sizeRuleMessage(maxFileSizeBytes)}
      </p>
    </div>
  );
};

/**
 * Uploads a cover and chooses its framing — or, given `existing`, re-frames the cover already
 * there without uploading anything.
 *
 * There is no crop any more. The old 16:9 crop cut the picture down in the browser before upload,
 * so whatever fell outside it was gone for good, and the banner (a much wider 1920x600) was then
 * cropped AGAIN around the middle of what was left. Now the whole image is kept and the member
 * picks a focal point; every crop the site makes of it centres there. Changing it later is
 * "Adjust framing", not a re-upload.
 *
 * Upload, then point: the file goes up untouched, and the point is saved against the media item
 * the server created. If the upload works but the point does not save, the key is kept so
 * "Save" retries only the point.
 */
export const HeroImageModal = ({ closeModal, altText, maxFileSizeBytes, existing = null }) => {
  const isAdjusting = !!existing;

  const [preview, setPreview] = useState(existing?.src ?? null);
  const [file, setFile] = useState(null);
  const [point, setPoint] = useState(existing?.point ?? CENTRE);
  const [mediaKey, setMediaKey] = useState(existing?.mediaKey ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [uploadError, setUploadError] = useState({
    sizeError: null,
    typeError: false,
    dimensionError: null,
    moderationError: null,
  });

  const clearErrors = () =>
    setUploadError({
      sizeError: null,
      typeError: false,
      dimensionError: null,
      moderationError: null,
    });

  const showServerError = (message) =>
    setUploadError((prev) => ({ ...prev, moderationError: message }));

  /**
   * Reads the file, then measures it before letting the member reach the framing stage.
   * Rejecting an under-sized image here rather than on submit means they are not asked to
   * frame a cover that was never going to be accepted.
   */
  function onSelectFile(selected) {
    if (!selected) {
      return;
    }

    clearErrors();

    /*
     * Size before the dimension probe, and before FileUploader's own maxSize gate — which is no
     * longer used, because its callback receives only a fixed "File size is too big" string and
     * so cannot name the file's actual size. Doing it here produces the server's exact message:
     * actual size, limit, remedy (T212719 criterion 2).
     */
    if (selected.size > maxFileSizeBytes) {
      setUploadError({
        sizeError: tooLargeMessage(selected.size, maxFileSizeBytes),
        typeError: false,
        dimensionError: null,
        moderationError: null,
      });
      setPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const dataUrl = reader.result?.toString() || "";
      const probe = new Image();

      probe.onload = () => {
        if (probe.naturalWidth < MIN_WIDTH || probe.naturalHeight < MIN_HEIGHT) {
          setUploadError((prev) => ({
            ...prev,
            dimensionError:
              `This image is ${probe.naturalWidth} x ${probe.naturalHeight}px. ` +
              `Cover images must be at least ${MIN_WIDTH} x ${MIN_HEIGHT}px.`,
          }));
          setPreview(null);
          return;
        }

        setFile(selected);
        setMediaKey(null);
        setPoint(CENTRE);
        setPreview(dataUrl);
      };

      // A file the browser cannot decode is not a usable cover, whatever its extension said.
      probe.onerror = () => {
        setUploadError((prev) => ({
          ...prev,
          typeError: true,
        }));
        setPreview(null);
      };

      probe.src = dataUrl;
    });

    reader.readAsDataURL(selected);
  }

  const saveHandler = async () => {
    // All of these must be present, not any one of them — the old `image || canvas || crop`
    // guard went on to dereference whichever was missing.
    if (!preview || (!file && !mediaKey)) {
      return;
    }

    setIsSaving(true);
    clearErrors();

    let key = mediaKey;

    if (!key) {
      const formData = new FormData();
      formData.append("HeroImage", file, file.name);

      try {
        const uploaded = await postHeroImage(formData);
        key = uploaded?.mediaKey ?? null;
        setMediaKey(key);
      } catch (error) {
        setIsSaving(false);
        showServerError(
          error?.response?.data?.message ||
            error?.message ||
            "The image could not be uploaded. Please try a different image."
        );
        return;
      }
    }

    const saved = key
      ? await postFocalPoint({ source: "cover", mediaKey: key, left: point.left, top: point.top })
      : { isSuccess: false, message: null };

    if (!saved.isSuccess) {
      setIsSaving(false);
      showServerError(
        isAdjusting
          ? saved.message
          : "Your cover was uploaded, but its framing could not be saved. Please press Save to try again."
      );
      return;
    }

    setIsDone(true);
    window.location.reload();
  };

  if (isSaving || isDone) {
    return (
      <div className="saved-colors-loading-container">
        <SharedIcon
          iconName="#sprite-spinner"
          className="animate-spin"
        />
      </div>
    );
  }

  return (
    <>
      <div className="hero-image__header">
        <h4 className="hero-image__title">
          {isAdjusting ? "Adjust cover framing" : "Upload a cover image"}
        </h4>
      </div>
      <div className="hero-image__main">
        {!preview && (
          <FileUploader
            name="file"
            classes={"hero-image__uploader"}
            handleChange={onSelectFile}
            children={<UploaderInner maxFileSizeBytes={maxFileSizeBytes} />}
            types={ACCEPTED_TYPES}
            onTypeError={() =>
              setUploadError({
                sizeError: null,
                typeError: true,
                dimensionError: null,
                moderationError: null,
              })
            }
          />
        )}
        {uploadError.sizeError && (
          <p className="hero-image__uploader-validation" data-testid="image-upload-error" role="alert">
            {uploadError.sizeError}
          </p>
        )}
        {uploadError.typeError && !uploadError.sizeError && (
          <p className="hero-image__uploader-validation" data-testid="image-upload-error" role="alert">
            Only PNG, JPG or JPEG allowed.
          </p>
        )}
        {uploadError.dimensionError && (
          <p className="hero-image__uploader-validation" data-testid="image-upload-error" role="alert">
            {uploadError.dimensionError}
          </p>
        )}
        {uploadError.moderationError && (
          <p className="hero-image__uploader-validation" data-testid="image-upload-error" role="alert">
            {uploadError.moderationError}
          </p>
        )}

        {!!preview && (
          <>
            <FocalPointPicker
              src={preview}
              alt="Cover image"
              value={point}
              onChange={setPoint}
              previews={COVER_PREVIEWS}
              testId="cover-image-focal-point"
            />

            {/*
              Alt text is auto-generated (see MemberProfileExtensions.GetCoverAltText). Showing
              it here is the "communicated at the point of upload" half of the requirement —
              the member learns how their cover will be described to screen readers.
            */}
            {!!altText && (
              <p className="hero-image__alt-text" data-testid="cover-image-alt-text">
                This image will be described as &ldquo;{altText}&rdquo;.
              </p>
            )}
          </>
        )}
      </div>
      <div className="hero-image__footer">
        <div className="hero-image__buttons-wrapper">
          <button onClick={closeModal} className="my-profile__submit-button">
            Cancel
          </button>
          <button
            onClick={saveHandler}
            className="my-profile__submit-button my-profile__submit-button--dark"
            disabled={!preview}
            data-testid="cover-image-submit"
          >
            {isAdjusting ? "Save framing" : "Upload"}
          </button>
        </div>
      </div>
    </>
  );
};
